import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { TypewriterText } from '../hooks/useTypewriter';

const API = '/api';

function headers() {
  const h = { 'Content-Type': 'application/json' };
  const t = localStorage.getItem('logos_session_token');
  if (t) h['Authorization'] = `Bearer ${t}`;
  return h;
}

const money = (cents) => `$${(cents / 100).toFixed(2)}`;
const KIND = { purchase: 'bought', grant: 'granted', reserve: 'held for a run', release: 'released', charge: 'charged', refund: 'refunded', adjust: 'adjusted' };

export default function Credits() {
  const { user, loading } = useAuth();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (!user) return;
    fetch(`${API}/credits`, { headers: headers(), credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null)).then(setData);
  }, [user]);

  const buy = async (dollars) => {
    setBusy(dollars); setError(null);
    const res = await fetch(`${API}/credits/checkout`, {
      method: 'POST', headers: headers(), credentials: 'include', body: JSON.stringify({ dollars }),
    });
    const body = await res.json().catch(() => ({}));
    setBusy(null);
    if (!res.ok) { setError(body.detail || 'that did not work'); return; }
    window.location.href = body.url;
  };

  if (loading) return <div className="min-h-screen bg-black" />;
  if (!user) return <div className="min-h-screen bg-black flex items-center justify-center px-6"><p className="text-[#888888] lowercase">sign in to see your credit</p></div>;

  return (
    <div className="min-h-screen bg-black py-10 px-4 sm:px-6 lg:px-8 page-wrapper" data-testid="credits-page">
      <div className="max-w-3xl mx-auto space-y-10">
        <div>
          <h1 className="text-4xl lg:text-5xl font-bold text-white lowercase font-mono"><TypewriterText text="credit" speed={65} /></h1>
          <p className="text-[#aaaaaa] mt-3">cloud runs are paid from credit, in us dollars. a run holds its cap when it starts and is charged what it used when it ends.</p>
        </div>

        {params.get('paid') && <p className="font-mono text-sm text-white lowercase">payment received. it shows here once the provider confirms it.</p>}

        {data && (
          <>
            <div className="border-y border-[#1e251f] py-5 flex items-baseline gap-6 flex-wrap">
              <div>
                <div className="font-mono text-[10px] text-[#666666] lowercase tracking-wide">balance</div>
                <div className="font-mono text-3xl text-white tabular-nums mt-1">{data.house ? 'house' : money(data.balance_cents)}{!data.house && <span className="text-base text-[#666666] ml-2">usd</span>}</div>
              </div>
              <p className="font-mono text-xs text-[#666666] lowercase">
                {data.house ? 'your runs never touch credit.' : 'training is paid from credit, by the hour.'}
              </p>
            </div>

            {(data.payments || !data.house) && (
              <section className="space-y-3">
                <h2 className="font-mono text-[11px] text-[#666666] lowercase tracking-wide">add credit</h2>
                <div className="flex gap-2 flex-wrap">
                  {data.packs.map((d) => (
                    <button key={d} onClick={() => buy(d)} disabled={!data.payments || busy !== null}
                      className="border border-[#383f38] px-5 py-2 rounded-full font-mono text-sm text-white hover:bg-white hover:text-black transition-all disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-white">
                      {busy === d ? 'opening…' : `$${d} usd`}
                    </button>
                  ))}
                </div>
                {!data.payments && <p className="font-mono text-xs text-[#555555] lowercase">buying credit isn't open yet.</p>}
                {data.house && <p className="font-mono text-xs text-[#555555] lowercase">your runs never use credit; buying here is only useful as a test.</p>}
                {error && <p className="font-mono text-xs text-[#ff5c5c] lowercase">{error}</p>}
              </section>
            )}

            <section>
              <h2 className="font-mono text-[11px] text-[#666666] lowercase tracking-wide mb-2">ledger</h2>
              {data.entries.length === 0 ? (
                <p className="font-mono text-xs text-[#555555] lowercase">nothing yet</p>
              ) : (
                <div className="divide-y divide-[#1a201b] border-y border-[#1a201b] font-mono text-sm">
                  {data.entries.map((e, i) => (
                    <div key={i} className="py-2.5 flex items-baseline gap-3">
                      <span className="text-[#666666] text-xs w-24 shrink-0">{new Date(e.at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }).toLowerCase()}</span>
                      <span className="text-[#aaaaaa] lowercase">{KIND[e.kind] || e.kind}</span>
                      {e.run_id && <Link to={`/runs/${e.run_id}`} className="text-[#555555] text-xs hover:text-white">{e.run_id.slice(4, 10)}</Link>}
                      <span className={`ml-auto tabular-nums ${e.amount_cents < 0 ? 'text-[#888888]' : 'text-white'}`}>
                        {e.amount_cents < 0 ? '−' : '+'}{money(Math.abs(e.amount_cents))}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}
